import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { Eye, EyeSlash, CheckCircleFill } from 'react-bootstrap-icons';
import apiClient, { API_ENDPOINTS } from '../api/config';
import { useAuth } from '../contexts/AuthContext';
import { evaluatePasswordRequirements, PasswordRequirement } from '../utils/passwordPolicy';
import './auth-shared.css';

const TEMP_PASSWORD_RESET_FLAG = 'tempPasswordResetRequired';
const TEMP_PASSWORD_RESET_EMAIL = 'tempPasswordResetEmail';
const AUTH_NOTICE_MESSAGE = 'authNoticeMessage';
const PASSWORD_CHANGED_NOTICE = 'Password changed successfully. Please sign in again.';

/* ===========================================================
   Set Password Page Component
   Shown after email verification (or first login with a
   temporary password) so the user can set their permanent
   password. Includes a real-time strength meter and a
   requirements checklist.
   =========================================================== */

/**
 * Calculates password strength as a fraction (0-1) based on requirements met.
 */
function calculateStrength(requirements: PasswordRequirement[]): number
{
    const metCount = requirements.filter((r) => r.met).length;
    return metCount / requirements.length;
}

/**
 * Returns a human-readable strength label.
 */
function getStrengthLabel(strength: number): string
{
    if (strength === 0)
    {
        return '';
    }
    if (strength <= 0.25)
    {
        return 'Weak Password';
    }
    if (strength <= 0.5)
    {
        return 'Fair Password';
    }
    if (strength <= 0.75)
    {
        return 'Good Password';
    }
    return 'Strong Password';
}

/**
 * Returns the CSS class for the strength bar colour.
 */
function getStrengthClass(strength: number): string
{
    if (strength <= 0.25)
    {
        return 'sp-strength-weak';
    }
    if (strength <= 0.5)
    {
        return 'sp-strength-fair';
    }
    if (strength <= 0.75)
    {
        return 'sp-strength-good';
    }
    return 'sp-strength-strong';
}

/* ---------- Main component ---------- */

const SetPasswordNew: React.FC = () =>
{
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    /* -- Form state -- */
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');

    /* -- Visibility toggles -- */
    const [showNew, setShowNew] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    /* -- UI state -- */
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState<boolean>(false);

    /* -- Derived values -- */
    const requirements = evaluatePasswordRequirements(newPassword, [user?.email, user?.name]);
    const strength = calculateStrength(requirements);
    const strengthLabel = getStrengthLabel(strength);
    const allRequirementsMet = requirements.every((r) => r.met);
    const currentUserEmail = (user?.email || '').trim().toLowerCase();
    const resetFlowEmail = (sessionStorage.getItem(TEMP_PASSWORD_RESET_EMAIL) || '').trim().toLowerCase();
    const hasPendingResetSession =
        sessionStorage.getItem(TEMP_PASSWORD_RESET_FLAG) === 'true' &&
        (!resetFlowEmail || !currentUserEmail || currentUserEmail === resetFlowEmail);
    const isTemporaryPasswordReset = Boolean(user?.must_change_password) || hasPendingResetSession;

    /* ---------- Event handlers ---------- */

    /**
     * Toggles visibility for the new-password field.
     */
    function handleToggleNew(): void
    {
        setShowNew((prev) => !prev);
    }

    /**
     * Toggles visibility for the confirm-password field.
     */
    function handleToggleConfirm(): void
    {
        setShowConfirm((prev) => !prev);
    }

    /**
     * Submits the new password to the backend.
     */
    async function handleSubmit(e: React.FormEvent): Promise<void>
    {
        e.preventDefault();
        setSubmitted(true);
        setError(null);

        /* Client-side validation */
        if (!allRequirementsMet)
        {
            setError('Please meet all password requirements.');
            return;
        }

        if (newPassword !== confirmPassword)
        {
            setError('Passwords do not match.');
            return;
        }

        setSaving(true);

        try
        {
            const payload = {
                new_password: newPassword,
                confirm_password: confirmPassword,
            };
            await apiClient.post(API_ENDPOINTS.CHANGE_PASSWORD, payload);
            sessionStorage.setItem(AUTH_NOTICE_MESSAGE, PASSWORD_CHANGED_NOTICE);
            logout();
            navigate('/login', {
                replace: true,
                state: { authNotice: PASSWORD_CHANGED_NOTICE },
            });
        }
        catch (err: any)
        {
            const detail =
                err.response?.data?.detail ||
                'Failed to set password. Please try again.';
            setError(detail);
        }
        finally
        {
            setSaving(false);
        }
    }

    /* ---------- Render helpers ---------- */

    /**
     * Renders the password strength progress bar.
     */
    function renderStrengthBar(): React.ReactNode
    {
        if (!newPassword)
        {
            return null;
        }

        return (
            <div className="sp-strength-container">
                <div className="sp-strength-track">
                    <div
                        className={`sp-strength-fill ${getStrengthClass(strength)}`}
                    />
                </div>
                <span className="sp-strength-label">{strengthLabel}</span>
            </div>
        );
    }

    /**
     * Renders the individual password requirement checklist items.
     */
    function renderRequirements(): React.ReactNode
    {
        return (
            <ul className="sp-requirements-list">
                {requirements.map((req) => (
                    <li
                        key={req.label}
                        className={req.met ? 'sp-req-met' : 'sp-req-unmet'}
                    >
                        <CheckCircleFill size={12} />
                        <span>{req.label}</span>
                    </li>
                ))}
            </ul>
        );
    }

    /* ---------- Main render ---------- */

    return (
        <div className="sp-page">
            <div className="auth-card sp-card">

                {/* Account Verified badge */}
                <div className="sp-verified-badge">
                    <CheckCircleFill size={14} />
                    <span>Account Verified</span>
                </div>

                {/* Heading */}
                <h2 className="sp-heading">Set your new password</h2>

                {/* Info box */}
                <div className="sp-info-box">
                    <strong>{isTemporaryPasswordReset ? 'Your temporary password will be replaced.' : 'Choose a secure password.'}</strong>{' '}
                    {isTemporaryPasswordReset
                        ? "This new password is what you'll use every time you log in to AcceliQA."
                        : 'This password will be used for future sign-ins to AcceliQA.'}
                </div>

                {/* Error message */}
                {error && (
                    <div className="auth-server-error">{error}</div>
                )}

                {/* Form */}
                <Form onSubmit={handleSubmit} noValidate>

                    {/* New Password */}
                    <div className="auth-form-group">
                        <Form.Label className="auth-label">New Password</Form.Label>
                        <div className="password-input-wrapper">
                            <Form.Control
                                type={showNew ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                                className="password-input-field"
                                isInvalid={submitted && !allRequirementsMet}
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={handleToggleNew}
                                aria-label={showNew ? 'Hide password' : 'Show password'}
                            >
                                {showNew ? <EyeSlash size={18} /> : <Eye size={18} />}
                            </button>
                        </div>

                        {/* Strength bar */}
                        {renderStrengthBar()}

                        {/* Requirements checklist */}
                        {renderRequirements()}
                    </div>

                    {/* Confirm New Password */}
                    <div className="auth-form-group">
                        <Form.Label className="auth-label">Confirm New Password</Form.Label>
                        <div className="password-input-wrapper">
                            <Form.Control
                                type={showConfirm ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                className="password-input-field"
                                isInvalid={submitted && newPassword !== confirmPassword}
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={handleToggleConfirm}
                                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                            >
                                {showConfirm ? <EyeSlash size={18} /> : <Eye size={18} />}
                            </button>
                        </div>

                        {/* Mismatch error */}
                        {submitted && newPassword !== confirmPassword && (
                            <div className="field-error-text">Passwords do not match</div>
                        )}
                    </div>

                    {/* Submit button */}
                    <button
                        type="submit"
                        className="auth-submit-btn"
                        disabled={saving}
                    >
                        {saving
                            ? <Spinner as="span" animation="border" size="sm" />
                            : 'Set My Password \u2192'
                        }
                    </button>
                </Form>
            </div>
        </div>
    );
};

export default SetPasswordNew;
